import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { KfProfileCard } from "../knightsfee/KfProfileCard";
import { ExternalLink, Loader2 } from "lucide-react";

// Identity settings, tied directly into the Knights Fee profile (same avatar/bio the rest
// of Call to Arms sees on your posts, listings, etc.) — reuses KfProfileCard as-is so avatar
// upload/bio editing never drifts out of sync with the Knights Fee page itself.
export function ProfileTab() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/kf/profile/${user.id}`);
    setProfile(data);
    setNameDraft(data.name);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveName = async () => {
    const name = nameDraft.trim();
    if (!name) return toast.error("Name can't be empty");
    if (name === profile.name) return;
    setSavingName(true);
    try {
      await api.patch("/kf/profile", { name });
      setProfile((p) => ({ ...p, name }));
      await checkAuth();
      toast.success("Name updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update your name");
    } finally {
      setSavingName(false);
    }
  };

  const onProfileUpdate = async (updated) => {
    setProfile(updated);
    await checkAuth();
  };

  if (!profile) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div>;
  }

  return (
    <div data-testid="settings-profile-tab" className="space-y-6">
      <div>
        <h2 className="font-display text-2xl mb-1">Profile</h2>
        <p className="text-sm text-[#f1e0c5]/50">Your identity across Call to Arms — this is exactly what fellow Knights see on Knights Fee.</p>
      </div>

      <div id="settings-section-name" className="rounded-2xl bg-black/40 border border-white/10 p-6 space-y-2">
        <label className="text-xs uppercase tracking-wider text-[#f1e0c5]/40">Display Name</label>
        <div className="flex gap-2">
          <Input
            data-testid="settings-name-input"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            maxLength={60}
            className="bg-black/30 border-white/10"
          />
          <Button data-testid="settings-save-name-button" onClick={saveName} disabled={savingName || nameDraft.trim() === profile.name} className="rounded-full bg-[#D4AF37] text-black hover:bg-[#c19b2e] shrink-0">
            {savingName ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div id="settings-section-avatar">
        <KfProfileCard profile={profile} onProfileUpdate={onProfileUpdate} onFollowChange={onProfileUpdate} />
      </div>

      <button
        data-testid="settings-view-public-profile-link"
        id="settings-section-public-profile"
        onClick={() => navigate(`/knights-fee/u/${user.id}`)}
        className="flex items-center gap-2 text-sm text-[#D4AF37] hover:underline"
      >
        <ExternalLink className="w-4 h-4" /> View My Public Knights Fee Profile
      </button>
    </div>
  );
}
